import * as FileSystem from 'expo-file-system';
import { Platform } from 'react-native';
import axios from 'axios';
import { API_CONFIG, MOCK_MODE, TIMEOUTS } from '../constants/config';
import { getToken, saveToken } from '../utils/storage';
import {
  createTokenRefreshInterceptor,
  getValidAccessToken,
  getAxiosAuthConfig,
} from '../utils/tokenInterceptor';

// Generate hash for filename
const generateHash = (input: string): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
};

// Generate unique filename to prevent collisions
const generateUniqueFilename = (
  originalName: string,
  extension: string
): string => {
  const hash = generateHash(originalName + Date.now());
  const cleanName = originalName
    .replace(/[^a-zA-Z0-9]/g, '-')
    .toLowerCase()
    .substring(0, 20);
  return `${cleanName}-${hash}${extension}`;
};

// Palette match values - keep in sync with backend enum
export const PALETTE_MATCH_VALUES = {
  EXCELLENT: 'excellent' as const,
  GOOD: 'good' as const,
  NEUTRAL: 'neutral' as const,
  POOR: 'poor' as const,
} as const;

export type PaletteMatchValue =
  (typeof PALETTE_MATCH_VALUES)[keyof typeof PALETTE_MATCH_VALUES];

export type WardrobeItem = {
  id: string;
  name: string;
  image: string;
  tags: string[];
  paletteMatch: PaletteMatchValue;
  createdAt: string;
};

// Create axios instance with configuration
const api = axios.create({
  ...API_CONFIG,
  timeout: TIMEOUTS.API_REQUEST,
  ...getAxiosAuthConfig(),
});

// Add auth token to requests (skip when cookie mode - browser sends cookies)
api.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token && token !== 'cookie') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle API errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Wardrobe API Error:', error.response?.data || error.message);
    throw error;
  }
);

// Add token refresh interceptor
createTokenRefreshInterceptor(api);

// Get current user from stored user data
const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const userData = await getToken('user_data');
    if (userData) {
      const user = JSON.parse(userData);
      return user.id || user.email; // Use ID or email as fallback
    }
    return null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// User-specific storage keys
const getUserWardrobeKey = (userId: string) => `wardrobe_items_${userId}`;

// Get user-specific wardrobe items from storage
const getUserWardrobeItems = async (
  userId: string
): Promise<WardrobeItem[]> => {
  try {
    const userWardrobeKey = getUserWardrobeKey(userId);
    const storedItems = await getToken(userWardrobeKey);

    if (storedItems) {
      return JSON.parse(storedItems);
    } else {
      // Return empty array for new users
      return [];
    }
  } catch (error) {
    console.error('Error getting user wardrobe items:', error);
    return [];
  }
};

// Save user-specific wardrobe items to storage
const saveUserWardrobeItems = async (
  userId: string,
  items: WardrobeItem[]
): Promise<void> => {
  try {
    const userWardrobeKey = getUserWardrobeKey(userId);
    await saveToken(userWardrobeKey, JSON.stringify(items));
  } catch (error) {
    console.error('Error saving user wardrobe items:', error);
  }
};

/**
 * Get all wardrobe items for the user
 */
export const getWardrobeItems = async (): Promise<WardrobeItem[]> => {
  const userId = await getCurrentUserId();

  if (MOCK_MODE) {
    if (userId) {
      return await getUserWardrobeItems(userId);
    } else {
      return []; // No user, return empty array
    }
  }

  try {
    const response = await api.get<WardrobeItem[]>('/wardrobe');
    return response.data;
  } catch (err: unknown) {
    console.warn('Failed to fetch wardrobe items from backend:', err);

    // If the endpoint doesn't exist (404) or other errors, fall back to local storage
    if (userId) {
      console.log('Falling back to local storage for wardrobe items');
      return await getUserWardrobeItems(userId);
    } else {
      return []; // No user, return empty array
    }
  }
};

/**
 * Add a new item to the wardrobe
 */
export const addWardrobeItem = async (
  imageUri: string,
  name: string,
  tags: string[]
): Promise<WardrobeItem> => {
  // Validate that image is provided
  if (!imageUri || imageUri.trim() === '') {
    throw new Error('Image is required to create a wardrobe item');
  }

  // Validate the image exists (only on native platforms)
  if (Platform.OS !== 'web') {
    try {
      const fileInfo = await FileSystem.getInfoAsync(imageUri);
      if (!fileInfo.exists) {
        throw new Error('Image file does not exist');
      }
    } catch (error) {
      console.warn('Could not validate file existence:', error);
      // On web or if file validation fails, we'll proceed anyway
    }
  }

  if (MOCK_MODE) {
    // In mock mode, still require image but just create local item
    const newItem: WardrobeItem = {
      id: Date.now().toString(),
      name,
      image: imageUri,
      tags,
      paletteMatch: PALETTE_MATCH_VALUES.NEUTRAL, // Default to neutral in mock mode
      createdAt: new Date().toISOString(),
    };

    // Save to local storage
    const userId = await getCurrentUserId();
    if (userId) {
      const existingItems = await getUserWardrobeItems(userId);
      const updatedItems = [newItem, ...existingItems];
      await saveUserWardrobeItems(userId, updatedItems);
    }

    return newItem;
  }

  try {
    // Create form data for image upload
    const formData = new FormData();

    const originalFilename = imageUri.split('/').pop() || 'wardrobe-item.jpg';
    const match = /\.(\w+)$/.exec(originalFilename);
    const extension = match ? `.${match[1]}` : '.jpg';
    const type = match ? `image/${match[1]}` : 'image/jpeg';

    // Generate hashed filename to prevent collisions
    const hashedFilename = generateUniqueFilename(name, extension);

    if (Platform.OS === 'web') {
      // For web, convert the image URI to a Blob
      try {
        const response = await fetch(imageUri);
        const blob = await response.blob();

        // Use the hashed filename, but update extension if needed for data URIs
        let finalFilename = hashedFilename;
        if (imageUri.startsWith('data:image/')) {
          // Extract format from data URI (e.g., data:image/jpeg;base64,...)
          const formatMatch = imageUri.match(/data:image\/([^;]+)/);
          if (formatMatch) {
            const format = formatMatch[1];
            const newExtension = format === 'jpeg' ? '.jpg' : `.${format}`;
            finalFilename = generateUniqueFilename(name, newExtension);
          }
        } else if (blob.type && !hashedFilename.includes('.')) {
          // Extract format from blob type if extension missing
          const format = blob.type.split('/')[1];
          const newExtension = format === 'jpeg' ? '.jpg' : `.${format}`;
          finalFilename = generateUniqueFilename(name, newExtension);
        }

        formData.append('image', blob, finalFilename);
      } catch (error) {
        console.error('Failed to convert image to blob:', error);
        throw new Error('Failed to process image for upload');
      }
    } else {
      // For React Native
      // @ts-ignore: FormData typings in React Native
      formData.append('image', {
        uri: imageUri,
        name: hashedFilename,
        type,
      });
    }

    formData.append('name', name);
    formData.append('tags', JSON.stringify(tags));

    const response = await api.post<WardrobeItem>('/wardrobe', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: TIMEOUTS.IMAGE_UPLOAD,
    });

    return response.data;
  } catch (err: unknown) {
    console.error('Failed to add wardrobe item:', err);

    const response =
      err && typeof err === 'object' && 'response' in err
        ? (
            err as {
              response?: { status?: number; data?: { detail?: string } };
            }
          ).response
        : undefined;
    const message =
      err instanceof Error
        ? err.message
        : ((err as { message?: string })?.message ?? '');

    // Check for various backend errors that should trigger fallback to local storage
    const shouldFallbackToLocal =
      response?.status === 404 || // Endpoint doesn't exist
      response?.status === 400 || // Bad request (storage issues)
      response?.status === 500 || // Internal server error
      message?.includes('storage') || // Storage-related errors
      response?.data?.detail?.includes('storage') || // Backend storage errors
      response?.data?.detail?.includes('Failed to access storage bucket'); // Specific storage bucket error

    if (shouldFallbackToLocal) {
      console.warn(
        'Backend storage failed, falling back to local storage:',
        response?.data?.detail || message
      );

      const userId = await getCurrentUserId();
      if (userId) {
        // Create a local item and save it to user's wardrobe
        const newItem: WardrobeItem = {
          id: Date.now().toString(),
          name,
          image: imageUri,
          tags,
          paletteMatch: PALETTE_MATCH_VALUES.NEUTRAL, // Default to neutral when backend issues occur
          createdAt: new Date().toISOString(),
        };

        // Add to user's stored wardrobe items
        const existingItems = await getUserWardrobeItems(userId);
        const updatedItems = [newItem, ...existingItems];
        await saveUserWardrobeItems(userId, updatedItems);

        console.log('✅ Item saved to local storage successfully:', newItem);
        return newItem;
      } else {
        throw new Error('User not authenticated');
      }
    }

    // For other errors, re-throw
    throw err;
  }
};

/**
 * Delete a wardrobe item
 */
export const deleteWardrobeItem = async (id: string): Promise<void> => {
  if (MOCK_MODE) {
    // In mock mode, remove from local storage
    const userId = await getCurrentUserId();
    if (userId) {
      const existingItems = await getUserWardrobeItems(userId);
      const updatedItems = existingItems.filter((item) => item.id !== id);
      await saveUserWardrobeItems(userId, updatedItems);
    }
    return;
  }

  try {
    await api.delete(`/wardrobe/${id}`);
  } catch (err: unknown) {
    console.error('Failed to delete wardrobe item:', err);

    const status =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;

    // If the endpoint doesn't exist (404), just return success
    if (status === 404) {
      console.warn('Wardrobe endpoint not found, simulating successful delete');
      return;
    }

    // For other errors, re-throw
    throw err;
  }
};

/**
 * Get wardrobe items matching a specific palette
 */
export const getWardrobeItemsByPaletteMatch = async (
  match: PaletteMatchValue
): Promise<WardrobeItem[]> => {
  const userId = await getCurrentUserId();

  if (MOCK_MODE) {
    if (userId) {
      const userItems = await getUserWardrobeItems(userId);
      return userItems.filter((item) => item.paletteMatch === match);
    } else {
      return []; // No user, return empty array
    }
  }

  try {
    const response = await api.get<WardrobeItem[]>(`/wardrobe/match/${match}`);
    return response.data;
  } catch (err: unknown) {
    console.warn('Failed to fetch wardrobe items by palette match:', err);

    const status =
      err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;

    // If the endpoint doesn't exist (404), fall back to local storage
    if (status === 404) {
      console.warn(
        'Wardrobe endpoint not found, falling back to local storage'
      );
      if (userId) {
        const userItems = await getUserWardrobeItems(userId);
        return userItems.filter(
          (item: WardrobeItem) => item.paletteMatch === match
        );
      } else {
        return []; // No user, return empty array
      }
    }

    // For other errors, re-throw
    throw error;
  }
};
